import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { tDynamic } from '@/i18n'
import { useAuthStore } from '@/stores/authStore'
import { useConfig } from '@/hooks/useConfig'
import { Button } from '@/components/ui/button'
import {
  buildOAuthStartUrl,
  markOAuthRetried,
  sanitizeReturnTo,
  shouldRetryOAuthInteractively,
  suppressOAuthAutoStart,
} from '@/lib/oauthAutoStart'

/**
 * Wrks OAuth 로그인 완료 페이지 — 백엔드 콜백(/api/auth/oauth/callback)이 code 교환과 세션 쿠키
 * 설정을 마친 뒤 여기로 302 한다. URL 에는 code 가 없다 (`?ok=1` 또는 `?error=<code>`, 둘 다
 * 로그인을 시작한 경로가 있으면 `&returnTo`).
 * - ok: /auth/me 로 사용자를 읽어 스토어를 채우고 returnTo 로 이동
 * - 자동 시도(prompt=none)가 OAUTH_LOGIN_REQUIRED 로 돌아오면 prompt 없이 한 번 더 시도한다
 *   (웍스 세션은 살아 있고 로그인 쿠키만 없으면 웍스가 쿠키를 다시 설정하고 바로 돌려보낸다)
 * - 그 밖의 error: 현재 언어의 메시지 + 다시 시도 / 웍스로 돌아가기 (자동 시도는 이 세션에서 중단)
 */
export function OAuthCallback() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { data: config } = useConfig()
  const completeOAuthLogin = useAuthStore((state) => state.completeOAuthLogin)

  const errorCode = params.get('error')
  const ok = params.get('ok') === '1'
  const returnTo = sanitizeReturnTo(params.get('returnTo')) ?? '/'
  const [failure, setFailure] = useState<string | null>(null)
  // 마운트 때 한 번만 판단한다: 재시도하면서 표식을 바꾸므로 렌더마다 읽으면 오류 화면이 비친다
  const [retrying] = useState(() => shouldRetryOAuthInteractively(errorCode))
  const started = useRef(false)

  useEffect(() => {
    if (retrying) {
      if (started.current) return
      started.current = true
      markOAuthRetried()
      window.location.replace(buildOAuthStartUrl(config, { returnTo }))
      return
    }
    if (errorCode) {
      suppressOAuthAutoStart()
      return
    }
    if (!ok || started.current) return
    started.current = true
    completeOAuthLogin()
      .then(() => navigate(returnTo, { replace: true }))
      .catch((err: unknown) => {
        suppressOAuthAutoStart()
        setFailure(err instanceof Error && err.message ? err.message : t('pages.ssoAuthFailed'))
      })
  }, [retrying, config, errorCode, ok, returnTo, completeOAuthLogin, navigate, t])

  if (!errorCode && !ok) {
    return <Navigate to="/" replace />
  }

  if ((errorCode && !retrying) || failure) {
    const message = failure ?? tDynamic(`errors.${errorCode}`) ?? t('pages.ssoAuthFailed')
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-surface">
        <div className="text-center space-y-4 p-8">
          <div className="text-destructive text-lg font-medium">{message}</div>
          <p className="text-caption">{t('pages.ssoContactAdmin')}</p>
          <div className="flex justify-center gap-2">
            <Button onClick={() => window.location.assign(buildOAuthStartUrl(config, { returnTo }))}>
              {t('pages.oauthRetry')}
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.assign(config?.loginUrl ?? 'https://wrks.ai/')}
            >
              {t('pages.ssoBackToWrks')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-surface">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <p className="text-body">{retrying ? t('pages.ssoAuthenticating') : t('pages.oauthCompleting')}</p>
      </div>
    </div>
  )
}
